"use client";

import * as React from "react";
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";
import { Order } from "@/types/AppTypes";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
const chartData = [
  // Previous months (for 90d filter)
  { month: "Apr", sales: 3000, date: "2024-04-01" },
  { month: "May", sales: 2000, date: "2024-05-01" },
  // June weekly data (for 30d and 7d filters)
  { month: "Jun", sales: 2200, date: "2024-06-01" },
  { month: "Jun", sales: 2300, date: "2024-06-08" },
  { month: "Jun", sales: 4400, date: "2024-06-15" },
  { month: "Jun", sales: 2500, date: "2024-06-22" },
  { month: "Jun", sales: 3550, date: "2024-06-23" },
  { month: "Jun", sales: 4560, date: "2024-06-24" },
  { month: "Jun", sales: 2570, date: "2024-06-25" },
  { month: "Jun", sales: 2580, date: "2024-06-26" },
  { month: "Jun", sales: 4590, date: "2024-06-27" },
  { month: "Jun", sales: 2600, date: "2024-06-28" },
  { month: "Jun", sales: 1610, date: "2024-06-29" },
];

const chartConfig = {
  visitors: {
    label: "Visitors",
  },
  desktop: {
    label: "Desktop",
    color: "hsl(var(--chart-1))",
  },
  mobile: {
    label: "Mobile",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

const AnalyticsChart = ({ data }: { data: Order[] }) => {
  const isMobile = useIsMobile();
  const [timeRange, setTimeRange] = React.useState("30d");

  // Transform order data into chart data
  const chartData = React.useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    // Group by date (YYYY-MM-DD)
    const salesByDate: Record<
      string,
      { date: string; sales: number; month: string }
    > = {};
    data.forEach((order) => {
      const dateStr = order.created_at
        ? order.created_at.slice(0, 10)
        : undefined;
      if (!dateStr) return;
      // Try to get price from variant JSON
      let price = 0;
      try {
        const variant =
          typeof order.variant === "string"
            ? JSON.parse(order.variant)
            : order.variant;
        price = variant?.price ?? 0;
      } catch {
        price = 0;
      }
      const sales = (order.quantity ?? 0) * price;
      const month = dateStr.slice(5, 7); // MM
      const year = dateStr.slice(0, 4); // YYYY
      const monthLabel = new Date(dateStr).toLocaleString("en-US", {
        month: "short",
      });
      const key = dateStr;
      if (!salesByDate[key]) {
        salesByDate[key] = { date: dateStr, sales: 0, month: monthLabel };
      }
      salesByDate[key].sales += sales;
    });
    // Convert to array and sort by date
    return Object.values(salesByDate).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
  }, [data]);

  // Filter chart data by time range
  const filteredData = React.useMemo(() => {
    if (!chartData.length) return [];
    const referenceDate = new Date();
    let daysToSubtract = 90;
    if (timeRange === "30d") {
      daysToSubtract = 30;
    } else if (timeRange === "7d") {
      daysToSubtract = 7;
    }
    const startDate = new Date(referenceDate);
    startDate.setDate(startDate.getDate() - daysToSubtract);
    return chartData.filter((item) => new Date(item.date) >= startDate);
  }, [chartData, timeRange]);

  React.useEffect(() => {
    if (isMobile) {
      setTimeRange("7d");
    }
  }, [isMobile]);

 

  return (
    <Card className="@container/card">
      <CardHeader className="relative">
        <CardTitle>Sales Overview</CardTitle>
        <CardDescription>
          <span className="@[540px]/card:block hidden">
            Monthly sales performance for the current year
          </span>
          <span className="@[540px]/card:hidden">Last 3 months</span>
        </CardDescription>
        <div className="absolute right-4 top-4">
          <ToggleGroup
            type="single"
            value={timeRange}
            onValueChange={setTimeRange}
            variant="outline"
            className="@[767px]/card:flex hidden"
          >
            <ToggleGroupItem value="90d" className="h-8 px-2.5">
              Last 3 months
            </ToggleGroupItem>
            <ToggleGroupItem value="30d" className="h-8 px-2.5">
              Last 30 days
            </ToggleGroupItem>
            <ToggleGroupItem value="7d" className="h-8 px-2.5">
              Last 7 days
            </ToggleGroupItem>
          </ToggleGroup>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger
              className="@[767px]/card:hidden flex w-40"
              aria-label="Select a value"
            >
              <SelectValue placeholder="Last 3 months" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="90d" className="rounded-lg">
                Last 3 months
              </SelectItem>
              <SelectItem value="30d" className="rounded-lg">
                Last 30 days
              </SelectItem>
              <SelectItem value="7d" className="rounded-lg">
                Last 7 days
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[250px] w-full"
        >
          <AreaChart data={filteredData} margin={{left:30, right:30}}>
            <defs>
              <linearGradient id="fillSales" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#a78bfa" stopOpacity={1.0} />
                <stop offset="95%" stopColor="#a78bfa" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              interval={0}
              tickFormatter={(dateStr) => {
                const date = new Date(dateStr);
                if (timeRange === "90d") {
                  return date.toLocaleString("en-US", { month: "short" });
                } else {
                  return date.toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                  });
                }
              }}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => value}
                  indicator="dot"
                />
              }
            />
            <Area
              dataKey="sales"
              type="natural"
              fill="url(#fillSales)"
              stroke="#a78bfa"
              strokeWidth={1.5}
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
export default AnalyticsChart;
